import type { ReactNode } from "react";

import type { UseTRPCMutationOptions } from "@blis/trpc/react";

import { toast } from "@/components/ui/toast";

/** Reads tRPC error code from a React Query / tRPC client error value. */
export function getTrpcErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const code = (error as { data?: { code?: unknown } }).data?.code;
  return typeof code === "string" ? code : undefined;
}

export function mutationResponse<
  TVariables,
  TError extends { message?: string },
  TData,
  TContext,
>(opts: {
  errorTitle: string;
  successMessage?: string | ((data?: TData) => string | ReactNode);
  onSuccess?: UseTRPCMutationOptions<
    TVariables,
    TError,
    TData,
    TContext
  >["onSuccess"];
  onError?: (error: TError) => void;
}): UseTRPCMutationOptions<TVariables, TError, TData, TContext> {
  return {
    onError: (error) => {
      opts.onError?.(error);
      toast.error(opts.errorTitle, { description: error.message });
    },
    onSuccess: (data, variables, result, context) => {
      opts.onSuccess?.(data, variables, result, context);
      if (opts.successMessage) {
        const message =
          typeof opts.successMessage === "string"
            ? opts.successMessage
            : opts.successMessage(data);
        toast.success(message);
      }
    },
  };
}
